// ── Client-side rule search ───────────────────────────────────────────────────
// Two result groups, like the Figma "Busca digitando" state:
//   Rules         — the query appears in a rule title
//   Mentioned in  — the query appears in a rule's body text (and not its title)
// The excerpt is the sentence that contains the hit, so the dropdown can bold it.

import { toPlainText } from '@portabletext/react';
import type { PortableTextBlock } from 'sanity';
import { stegaClean } from 'next-sanity';
import type { RulesBlock, RulesGroup, RulesRule } from '@/sanity/pages/PageGeneralRulesV3Template';

export interface FlatRule {
  slug: string;
  title: string;
  groupTitle: string;
  rule: RulesRule;
  /** First body sentence of the first variant — the sidebar/search summary. */
  summary: string;
  /** Every string the rule contains, joined; used for "Mentioned in". */
  text: string;
}

export interface SearchHit {
  rule: FlatRule;
  excerpt: string;
}

export interface SearchResults {
  rules: SearchHit[];
  mentioned: SearchHit[];
  total: number;
}

const plain = (v?: PortableTextBlock[]) => (v?.length ? toPlainText(v) : '');

function blockText(block: RulesBlock): string {
  switch (block._type) {
    case 'textBlock':
      return [block.heading, plain(block.body)].filter(Boolean).join('. ');
    case 'callout':
      return [block.title, plain(block.body)].filter(Boolean).join('. ');
    case 'statCards':
      return (block.cards ?? []).map((c) => [c.badge, c.value, plain(c.description)].join(' ')).join('. ');
    case 'breachSequence':
      return (block.steps ?? []).map((s) => [s.label, s.title, s.lines].join(' ')).join('. ');
    case 'timeCards':
      return (block.cards ?? []).map((c) => [c.label, c.value, c.suffix].join(' ')).join('. ');
    case 'productCards':
      return (block.cards ?? []).map((c) => [c.title, c.status, plain(c.body)].join(' ')).join('. ');
    case 'steps':
      return [
        block.title,
        ...(block.steps ?? []).map((s) => [s.title, plain(s.body)].join('. ')),
        block.tableLabel,
        ...(block.tableRows ?? []).map((r) => `${r.label} ${r.value}`),
        plain(block.tableNote),
      ]
        .filter(Boolean)
        .join('. ');
    case 'keyValueTable':
      return [block.label, ...(block.rows ?? []).map((r) => `${r.label} ${r.value}`), plain(block.note)]
        .filter(Boolean)
        .join('. ');
    default:
      return '';
  }
}

export function ruleSlug(rule: RulesRule): string {
  const raw = stegaClean(rule.slug?.current) || stegaClean(rule.title) || rule._key;
  return String(raw)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function flattenRules(groups: RulesGroup[] = []): FlatRule[] {
  const out: FlatRule[] = [];
  for (const group of groups) {
    for (const rule of group.rules ?? []) {
      const variants = rule.variants ?? [];
      const firstText = variants[0]?.sections?.[0]?.blocks?.find((b) => b._type === 'textBlock');
      const firstBody = firstText && firstText._type === 'textBlock' ? plain(firstText.body) : '';
      const summary = firstSentence(firstBody);
      const text = variants
        .flatMap((v) => [v.label, v.title, ...(v.appliesTo ?? []), ...(v.sections ?? []).flatMap((s) => (s.blocks ?? []).map(blockText))])
        .filter(Boolean)
        .join('. ');
      out.push({
        slug: ruleSlug(rule),
        title: stegaClean(rule.title) ?? '',
        groupTitle: stegaClean(group.title) ?? '',
        rule,
        summary,
        text: stegaClean(text),
      });
    }
  }
  return out;
}

function firstSentence(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  const m = clean.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : clean).trim();
}

/** Sentence around the first hit, shortened to ~`max` chars around the match. */
function excerptAround(text: string, query: string, max = 110): string {
  const clean = text.replace(/\s+/g, ' ');
  const idx = clean.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return firstSentence(clean);
  const start = Math.max(clean.lastIndexOf('. ', idx) + 1, 0);
  let endIdx = clean.indexOf('. ', idx);
  if (endIdx < 0) endIdx = clean.length;
  else endIdx += 1;
  let sentence = clean.slice(start, endIdx).trim();
  if (sentence.length > max) {
    const hitPos = sentence.toLowerCase().indexOf(query.toLowerCase());
    const from = Math.max(0, Math.min(hitPos - 30, sentence.length - max));
    sentence = (from > 0 ? '…' : '') + sentence.slice(from, from + max).trim() + '…';
  }
  return sentence;
}

export function searchRules(rules: FlatRule[], rawQuery: string): SearchResults {
  const query = rawQuery.trim().toLowerCase();
  if (query.length < 2) return { rules: [], mentioned: [], total: 0 };
  const titleHits: SearchHit[] = [];
  const bodyHits: SearchHit[] = [];
  for (const rule of rules) {
    if (rule.title.toLowerCase().includes(query)) {
      titleHits.push({ rule, excerpt: excerptAround(rule.text || rule.summary, query) });
    } else if (rule.text.toLowerCase().includes(query)) {
      bodyHits.push({ rule, excerpt: excerptAround(rule.text, query) });
    }
  }
  return { rules: titleHits, mentioned: bodyHits, total: titleHits.length + bodyHits.length };
}

/** Splits `text` into [before, match, after] chunks for highlighting. */
export function splitHighlight(text: string, query: string): { chunk: string; hit: boolean }[] {
  const q = query.trim().toLowerCase();
  if (!q) return [{ chunk: text, hit: false }];
  const out: { chunk: string; hit: boolean }[] = [];
  let rest = text;
  while (rest.length) {
    const i = rest.toLowerCase().indexOf(q);
    if (i < 0) {
      out.push({ chunk: rest, hit: false });
      break;
    }
    if (i > 0) out.push({ chunk: rest.slice(0, i), hit: false });
    out.push({ chunk: rest.slice(i, i + q.length), hit: true });
    rest = rest.slice(i + q.length);
  }
  return out;
}
